import { __decorate } from "tslib";
import { AnyDecoder, ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Token } from './Token.js';
/**
 * The multi-factor authentication methods a user can use as a second factor.
 */
export var MFAMethodType;
(function (MFAMethodType) {
    MFAMethodType["TOTP"] = "TOTP";
    MFAMethodType["Passkey"] = "Passkey";
    MFAMethodType["RecoveryCode"] = "RecoveryCode";
})(MFAMethodType || (MFAMethodType = {}));
export function getMFAMethodTypeName(method) {
    switch (method) {
        case MFAMethodType.TOTP:
            return $t(`%ZgY`);
        case MFAMethodType.Passkey:
            return $t(`%Zhp`);
        case MFAMethodType.RecoveryCode:
            return $t(`%ZhI`);
    }
}
/**
 * The WebAuthn Relying Party ID that passkeys are bound to.
 *
 * A passkey only works on the domain it was created for (or a subdomain of it), so all
 * passkeys are scoped to the dashboard domain. Returns null when no dashboard domain is
 * configured, in which case passkeys cannot be used at all.
 *
 * Shared between the client and the server on purpose: the server verifies against this
 * RP ID, and the client has to know whether the browser it runs in is allowed to use it.
 */
export function getWebauthnRpId() {
    const domain = STAMHOOFD.domains?.dashboard;
    if (!domain) {
        return null;
    }
    // The configured domain can carry a path and/or a port; an RP ID is a bare hostname.
    return domain.split('/')[0].split(':')[0] || null;
}
/**
 * Whether a browser served from `hostname` may create or use passkeys for our RP ID.
 *
 * WebAuthn only accepts an RP ID that is the origin's own domain or a registrable suffix
 * of it, so an organization on a custom or registration domain cannot touch the passkeys
 * that live on the dashboard domain.
 */
export function isWebauthnRpIdUsableOn(hostname) {
    const rpId = getWebauthnRpId();
    if (!rpId) {
        return false;
    }
    return hostname === rpId || hostname.endsWith('.' + rpId);
}
/**
 * Ask for the challenge that belongs to a pending login MFA token. Used by the SSO flow,
 * where the token comes back through a browser redirect instead of an error response.
 */
export class MFAChallengeRequest extends AutoEncoder {
    token = '';
}
__decorate([
    field({ decoder: StringDecoder })
], MFAChallengeRequest.prototype, "token", void 0);
/**
 * Sent as the `meta` of a `require_mfa` error when a password login succeeded but a
 * second factor is required. The client uses `token` to complete the `mfa` grant.
 */
export class MFAChallengeResponse extends AutoEncoder {
    /**
     * The short-lived, single-use MFA session token.
     */
    token = '';
    /**
     * The second factors the user has enrolled and can use to complete this login.
     */
    methods = [];
    /**
     * WebAuthn authentication options (PublicKeyCredentialRequestOptionsJSON) generated
     * by @simplewebauthn/server on OUR server, passed through to the browser's
     * startAuthentication(). This is trusted server output (not user input), so it is
     * kept opaque. Only set when the user has passkeys.
     */
    webauthnAuthenticationOptions = null;
}
__decorate([
    field({ decoder: StringDecoder })
], MFAChallengeResponse.prototype, "token", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(MFAMethodType)) })
], MFAChallengeResponse.prototype, "methods", void 0);
__decorate([
    field({ decoder: AnyDecoder, nullable: true })
], MFAChallengeResponse.prototype, "webauthnAuthenticationOptions", void 0);
/**
 * Sent as the `meta` of a `require_mfa_setup` error when a user is required to have 2FA
 * but has none enrolled yet. The client uses `setupToken` to authorize enrollment.
 */
export class MFASetupResponse extends AutoEncoder {
    setupToken = '';
    /**
     * Whether this account may enroll a passkey. There is no session yet at this point, so
     * the client cannot read it from the MFA status.
     */
    canUsePasskeys = false;
    /**
     * A normal session token, only handed out when the primary credential was a password
     * token (invite / password reset link). The user has no second factor yet, so anyone
     * holding that link could enroll a factor of their own and obtain a session anyway:
     * handing out the session right away is equally strong, and it lets the client first
     * walk the user through choosing a password before enrolling.
     *
     * Null for every other grant: there the client should complete the enrollment with
     * `setupToken` to obtain a session.
     */
    token = null;
}
__decorate([
    field({ decoder: StringDecoder })
], MFASetupResponse.prototype, "setupToken", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], MFASetupResponse.prototype, "canUsePasskeys", void 0);
__decorate([
    field({ decoder: Token, nullable: true })
], MFASetupResponse.prototype, "token", void 0);
/**
 * Returned by the TOTP setup endpoint. The `secret` is shown once so the user can add it
 * to their authenticator app; the client renders the QR code itself from `otpauthUri`.
 */
export class TOTPSetupResponse extends AutoEncoder {
    totpId = '';
    secret = '';
    otpauthUri = '';
}
__decorate([
    field({ decoder: StringDecoder })
], TOTPSetupResponse.prototype, "totpId", void 0);
__decorate([
    field({ decoder: StringDecoder })
], TOTPSetupResponse.prototype, "secret", void 0);
__decorate([
    field({ decoder: StringDecoder })
], TOTPSetupResponse.prototype, "otpauthUri", void 0);
/**
 * Request body to confirm a TOTP authenticator (verify the first code + name it).
 */
export class TOTPConfirmRequest extends AutoEncoder {
    code = '';
    name = '';
}
__decorate([
    field({ decoder: StringDecoder })
], TOTPConfirmRequest.prototype, "code", void 0);
__decorate([
    field({ decoder: StringDecoder })
], TOTPConfirmRequest.prototype, "name", void 0);
/**
 * A TOTP authenticator shown in the account settings list.
 */
export class TOTPCredential extends AutoEncoder {
    id = '';
    name = '';
    createdAt = new Date();
    lastUsedAt = null;
}
__decorate([
    field({ decoder: StringDecoder })
], TOTPCredential.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], TOTPCredential.prototype, "name", void 0);
__decorate([
    field({ decoder: DateDecoder })
], TOTPCredential.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], TOTPCredential.prototype, "lastUsedAt", void 0);
/**
 * A passkey shown in the account settings list.
 */
export class PasskeyCredential extends AutoEncoder {
    id = '';
    name = '';
    providerId;
    providerName;
    transports;
    createdAt = new Date();
    lastUsedAt = null;
    get derivedName() {
        return this.name || this.providerName || $t('%Zhp');
    }
    get icon() {
        if (this.providerName === '1Password') {
            return 'one-password';
        }
        if (this.providerName?.toLocaleLowerCase().includes('apple')) {
            return 'apple';
        }
        if (this.providerName?.toLocaleLowerCase().includes('google') || this.providerName?.toLocaleLowerCase().includes('chrome')) {
            return 'google';
        }
        if (this.providerName?.toLocaleLowerCase().includes('yubikey') || ((this.transports.includes('usb') || this.transports.includes('nfc')) && !this.transports.includes('hybrid'))) {
            return 'security-key';
        }
        return 'key';
    }
}
__decorate([
    field({ decoder: StringDecoder })
], PasskeyCredential.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PasskeyCredential.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PasskeyCredential.prototype, "providerId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PasskeyCredential.prototype, "providerName", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], PasskeyCredential.prototype, "transports", void 0);
__decorate([
    field({ decoder: DateDecoder })
], PasskeyCredential.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], PasskeyCredential.prototype, "lastUsedAt", void 0);
/**
 * A freshly generated batch of recovery codes. Only ever returned once, right after
 * generation. The server only stores hashes.
 */
export class RecoveryCodes extends AutoEncoder {
    codes = [];
}
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], RecoveryCodes.prototype, "codes", void 0);
/**
 * Overview of the user's enrolled factors, used to render the settings screen.
 */
export class MFAStatus extends AutoEncoder {
    totp = [];
    passkeys = [];
    hasRecoveryCodes = false;
    recoveryCodesRemaining = 0;
    /**
     * Whether this account may enroll passkeys. See User.canUsePasskeys: passkeys are bound
     * to a single domain, so they are limited to platform level accounts for now.
     */
    canUsePasskeys = false;
}
__decorate([
    field({ decoder: new ArrayDecoder(TOTPCredential) })
], MFAStatus.prototype, "totp", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(PasskeyCredential) })
], MFAStatus.prototype, "passkeys", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], MFAStatus.prototype, "hasRecoveryCodes", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], MFAStatus.prototype, "recoveryCodesRemaining", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], MFAStatus.prototype, "canUsePasskeys", void 0);
/**
 * Response of an enrollment action. `token` is only set during forced enrollment (the
 * setup-token flow), where completing the first factor also logs the user in.
 * `recoveryCodes` is only set when this was the user's first factor.
 */
export class MFAEnrollmentResult extends AutoEncoder {
    status = MFAStatus.create({});
    token = null;
    recoveryCodes = null;
}
__decorate([
    field({ decoder: MFAStatus })
], MFAEnrollmentResult.prototype, "status", void 0);
__decorate([
    field({ decoder: Token, nullable: true })
], MFAEnrollmentResult.prototype, "token", void 0);
__decorate([
    field({ decoder: RecoveryCodes, nullable: true })
], MFAEnrollmentResult.prototype, "recoveryCodes", void 0);
/**
 * WebAuthn registration options (PublicKeyCredentialCreationOptionsJSON) generated by
 * @simplewebauthn/server on OUR server, passed through to the browser's
 * startRegistration(). Trusted server output (not user input), kept opaque.
 */
export class WebauthnRegistrationOptions extends AutoEncoder {
    options = null;
}
__decorate([
    field({ decoder: AnyDecoder, nullable: true })
], WebauthnRegistrationOptions.prototype, "options", void 0);
// --- WebAuthn credential payloads (UNTRUSTED USER INPUT — fully decoded/validated) ---
/**
 * The inner `response` of a WebAuthn authentication assertion (browser output).
 */
export class WebauthnAssertionResponseData extends AutoEncoder {
    clientDataJSON = '';
    authenticatorData = '';
    signature = '';
    userHandle = null;
}
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAssertionResponseData.prototype, "clientDataJSON", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAssertionResponseData.prototype, "authenticatorData", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAssertionResponseData.prototype, "signature", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], WebauthnAssertionResponseData.prototype, "userHandle", void 0);
/**
 * A WebAuthn authentication assertion (AuthenticationResponseJSON) submitted by the
 * client to complete a passkey login. Decoded + validated as a structure.
 */
export class WebauthnAuthenticationCredential extends AutoEncoder {
    id = '';
    rawId = '';
    type = 'public-key';
    response = WebauthnAssertionResponseData.create({});
}
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAuthenticationCredential.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAuthenticationCredential.prototype, "rawId", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAuthenticationCredential.prototype, "type", void 0);
__decorate([
    field({ decoder: WebauthnAssertionResponseData })
], WebauthnAuthenticationCredential.prototype, "response", void 0);
/**
 * The inner `response` of a WebAuthn attestation (registration; browser output).
 */
export class WebauthnAttestationResponseData extends AutoEncoder {
    clientDataJSON = '';
    attestationObject = '';
    authenticatorData = null;
    transports = [];
    publicKeyAlgorithm = null;
    publicKey = null;
}
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAttestationResponseData.prototype, "clientDataJSON", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnAttestationResponseData.prototype, "attestationObject", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], WebauthnAttestationResponseData.prototype, "authenticatorData", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], WebauthnAttestationResponseData.prototype, "transports", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], WebauthnAttestationResponseData.prototype, "publicKeyAlgorithm", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], WebauthnAttestationResponseData.prototype, "publicKey", void 0);
/**
 * A WebAuthn registration attestation (RegistrationResponseJSON) submitted by the client
 * when enrolling a passkey. Decoded + validated as a structure.
 */
export class WebauthnRegistrationCredential extends AutoEncoder {
    id = '';
    rawId = '';
    type = 'public-key';
    response = WebauthnAttestationResponseData.create({});
}
__decorate([
    field({ decoder: StringDecoder })
], WebauthnRegistrationCredential.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnRegistrationCredential.prototype, "rawId", void 0);
__decorate([
    field({ decoder: StringDecoder })
], WebauthnRegistrationCredential.prototype, "type", void 0);
__decorate([
    field({ decoder: WebauthnAttestationResponseData })
], WebauthnRegistrationCredential.prototype, "response", void 0);
/**
 * The client's decoded registration credential returned by startRegistration(), plus a
 * user-provided name for the passkey.
 */
export class WebauthnRegistrationRequest extends AutoEncoder {
    response = WebauthnRegistrationCredential.create({});
    name;
}
__decorate([
    field({ decoder: WebauthnRegistrationCredential })
], WebauthnRegistrationRequest.prototype, "response", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], WebauthnRegistrationRequest.prototype, "name", void 0);
/**
 * Request body to rename an enrolled credential (TOTP authenticator or passkey).
 */
export class MFACredentialRenameRequest extends AutoEncoder {
    name = '';
}
__decorate([
    field({ decoder: StringDecoder })
], MFACredentialRenameRequest.prototype, "name", void 0);
//# sourceMappingURL=MFA.js.map